import React, {useState, useEffect, useRef} from 'react';
import cx from 'classnames';
import {useRouter} from 'next/router';
import {useTranslations, createTranslator} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import withMainLayout from 'layouts/main';
import {SectionPaddingMdToZero} from '@/section';
import Container from '@/container';
import Link from '@/links';
import {withBoldWeight} from '@/hocs/withFontType';
import {P, PMarginTMd, H2, PMarginSm} from '@/typography';
import {ListTerms} from '@/ui/lists/list-icons';
import {withMarginTopDoubleLg, withMarginBottomXxl, withMarginBottomMd} from '@/hocs/withSpacing';
import withBreakpoints from '@/hocs/withBreakpoints';
import {withVisibleDesktop993, withHiddenDesktop993} from '@/hocs/withResponsiveUtils';
import Breadcrumbs from '@/ui/breadcrumbs';
import Dropdown from '@/form-fields/dropdown';
import Tabs from '@/ui/tabs/tabs';
import LegalHero from '@/content/legal/hero';
import usePrivacyNoticeTabItems from '@/ui/tabs/tab-items/privacy-notice-tabs';
import legal from 'pages/legal/legal.module.scss';
import {pageMessagesProps} from 'helpers/i18n';
import {
  handleDropdown,
  scrollToTopOfPage,
  scrollableDropdownMob
} from '../components/ui/legal/legal.helper';
import shared from '../messages/en/shared.json';
import legalMessages from '../messages/en/legal.json';
import cookies from '../messages/en/cookies.json';

const seoMetas = props => {
  const seoT = createTranslator({locale: props.locale, messages: props.messages});
  return {
    title: seoT('cookies.seo.title'),
    description: seoT('cookies.seo.description'),
    canonical: seoT('cookies.seo.canonical')
  };
};

const List = props => <ListTerms {...props} />;
const BoldSpan = withBoldWeight('span');
const MarginedH2 = withMarginBottomMd(H2);
const PriorVersionMargin = withMarginTopDoubleLg('div');
const BreadcrumbsDesktop = withVisibleDesktop993(withMarginBottomXxl('div'));
const HiddenInDesktop = withHiddenDesktop993('div');

const CookiesPolicyPage = () => {
  const router = useRouter();
  const dropdownRef = useRef();
  const [isDropdownFixed, setIsDropdownFixed] = useState(false);
  const t = useTranslations('cookies');
  const legalT = useTranslations('legal');
  const tabItems = usePrivacyNoticeTabItems();

  const CookiesPolicyContent = () => (
    <>
      <MarginedH2>{t('heading')}</MarginedH2>
      <H2 className="heading-4">{t('sections.whatAreCookies.title')}</H2>
      <PMarginTMd>{t('sections.whatAreCookies.paragraph')}</PMarginTMd>
      <H2 className="heading-4">{t('sections.howWeUseCookies.title')}</H2>
      <PMarginTMd>{t('sections.howWeUseCookies.intro')}</PMarginTMd>
      <List>
        {t.raw('sections.howWeUseCookies.items').map(item => (
          <li key={item}>{item}</li>
        ))}
      </List>
      <H2 className="heading-4">{t('sections.cookiesInUse.title')}</H2>
      <PMarginTMd>
        {t.rich('sections.cookiesInUse.paragraph', {
          a: chunks => (
            <Link href="/cookies-list" target="_blank" variant="default">
              {chunks}
            </Link>
          )
        })}
      </PMarginTMd>
      <PMarginTMd>{t('sections.cookiesInUse.categoriesIntro')}</PMarginTMd>
      {t.raw('sections.cookiesInUse.categories').map(category => (
        <P key={category.label}>
          <BoldSpan>{category.label}</BoldSpan>: {category.text}
        </P>
      ))}
      <P>{t('sections.cookiesInUse.sessionIntro')}</P>
      {t.raw('sections.cookiesInUse.cookieTypes').map(cookieType => (
        <P key={cookieType.label}>
          <BoldSpan>{cookieType.label}</BoldSpan>: {cookieType.text}
        </P>
      ))}

      <H2 className="heading-4">{t('sections.thirdPartyCookies.title')}</H2>
      <PMarginTMd>{t('sections.thirdPartyCookies.paragraph')}</PMarginTMd>
      <P>{t('sections.thirdPartyCookies.optOutParagraph')}</P>

      <H2 className="heading-4">{t('sections.yourChoices.title')}</H2>
      {t.raw('sections.yourChoices.paragraphs').map((paragraph, index) => {
        const ParagraphTag = index === 0 ? PMarginTMd : P;
        return <ParagraphTag key={paragraph}>{paragraph}</ParagraphTag>;
      })}
      <H2 className="heading-4">{t('sections.moreInformation.title')}</H2>
      <PMarginTMd>
        {t.rich('sections.moreInformation.paragraph', {
          a1: chunks => (
            <Link
              href="https://ico.org.uk/for-the-public/online/cookies/"
              target="_blank"
              variant="default">
              {chunks}
            </Link>
          ),
          a2: chunks => (
            <Link href="https://www.allaboutcookies.org/" target="_blank" variant="default">
              {chunks}
            </Link>
          )
        })}
      </PMarginTMd>
      <P>
        {t.rich('contactParagraph', {
          a: chunks => (
            <Link href="mailto:support@workable.com" target="_top" variant="default">
              {chunks}
            </Link>
          )
        })}
      </P>
      <PriorVersionMargin className={legal['legal-content']}>
        <PMarginSm className="heading-6">
          <BoldSpan>{t('lastUpdated')}</BoldSpan>
        </PMarginSm>
      </PriorVersionMargin>
    </>
  );

  const ResponsiveMenu = withBreakpoints({
    desktop: () => (
      <Tabs
        activeTab="cookies-policy"
        title={legalT('sectionTitles.privacyNotice')}
        tabItems={tabItems}
        router={router}
      />
    ),
    mobile: () => (
      <div
        ref={dropdownRef}
        className={cx(isDropdownFixed ? legal['legal-dropdown-fixed'] : legal['legal-dropdown'])}>
        <Dropdown
          name="privacy-notice"
          defaultValue="cookies-policy"
          variant="green800"
          values={tabItems}
          onChange={item => handleDropdown(item, router)}
        />
      </div>
    )
  });

  useEffect(() => {
    scrollableDropdownMob(dropdownRef, setIsDropdownFixed);
  }, []);

  return (
    <>
      <LegalHero title={legalT('sectionTitles.privacyNotice')} />
      <SectionPaddingMdToZero>
        <Container>
          <Grid fluid>
            <BreadcrumbsDesktop>
              <Breadcrumbs
                clickableLink={{text: legalT('breadcrumb.legal'), urlCopy: '/legal'}}
                title={legalT('sectionTitles.privacyNotice')}
                activeTab={tabItems.find(item => item.value === 'cookies-policy').text}
              />
            </BreadcrumbsDesktop>
            <Row>
              <Col xs={12} md={12} lg={3}>
                <div>
                  <ResponsiveMenu />
                </div>
              </Col>
              <Col xs={12} md={12} lgOffset={1} lg={8}>
                <CookiesPolicyContent />
              </Col>
            </Row>
          </Grid>
          <HiddenInDesktop>
            <Link variant="go-to-top-arrow" onClick={scrollToTopOfPage} />
          </HiddenInDesktop>
        </Container>
      </SectionPaddingMdToZero>
    </>
  );
};

export default withMainLayout(CookiesPolicyPage, seoMetas);

export const messagesForIntl = {shared, legal: legalMessages, cookies};

export function getStaticProps() {
  return pageMessagesProps(messagesForIntl);
}
